<template>
<setup-wizard-component
	:sections="sections"
	:question-change-listener="questionChangeListener"
	route-segment="onboarding"
	v-on:activeQIndexChange="activeQIndexChange"
	v-on:activeSecIndexChange="activeSecIndexChange">

	<div v-if="sections[activeSecIndex].key == 'intro'" style="font-size: 16pt;font-weight: 300;">
		<p>This wizard will help you configure {{ $store.appName }} to best work with your company and how it operates.</p>
		<p>This step-by-step guide should take about 30 minutes. If you run into some areas that requires information that someone else in your company needs to provide, you can use the 'Share section link' button at the top to give them direct access to the setup guide.</p>
		<p>Your progress will be saved as you go, so feel free to take as long as you need to complete the setup process, but it must be finished in order for {{$store.appName}} to start providing value.</p>
		<p>To get started, click the button below!</p>
	</div>
	
	<employee-classifications 
		v-else-if="sections[activeSecIndex].key == 'employeeClassifications'" 
		:is-wizard-instance="true">
	</employee-classifications>
	
	<charge-out-groups 
		v-else-if="sections[activeSecIndex].key == 'chargeOutGroups'"
		:is-wizard-instance="true">
	</charge-out-groups>
	
	<settings-unions v-else-if="sections[activeSecIndex].key == 'unionSetup'" 
		:is-wizard-instance="true">		
	</settings-unions>
	
	<union-list v-else-if="sections[activeSecIndex].key == 'unionList'"
		v-on:doneSection="unionCreateDone">
	</union-list>

	<pay-adjustments 
		v-else-if="sections[activeSecIndex].key == 'payAdjustments'"
		:is-wizard-instance="true">
	</pay-adjustments>

	<revenue-code-manager-widget 
		v-else-if="sections[activeSecIndex].key == 'revenueCodes'"
		:is-wizard-instance="true">
	</revenue-code-manager-widget>

	<employee-mass-invite 
		v-else-if="sections[activeSecIndex].key == 'addEmployees'">
	</employee-mass-invite>

	<personnel-roles-widget
		v-else-if="sections[activeSecIndex].key == 'personnelRoles'"
		:is-wizard-instance="true">
	</personnel-roles-widget>

	<jobs-import
		v-else-if="sections[activeSecIndex].key == 'projectsImport'" :is-service="false" :key="sections[activeSecIndex].key">
	</jobs-import>

	<jobs-import
		v-else-if="sections[activeSecIndex].key == 'servicesImport'" :is-service="true" :key="sections[activeSecIndex].key">
	</jobs-import>

	<contacts-import
		v-else-if="sections[activeSecIndex].key == 'contactsImport'">
	</contacts-import>

	<material-importer
		v-else-if="sections[activeSecIndex].key == 'materialsImport'">
	</material-importer>

</setup-wizard-component>
</template>
<script type="text/javascript">
import SetupWizardComponent from "@/pages/setup_wizard/SetupWizardComponent"

import EmployeeClassifications from "@/pages/employees/EmployeeClassifications"
import ChargeOutGroups from "@/pages/employees/ChargeOutGroups"
import SettingsUnions from "@/pages/settings/SettingsUnions"
import UnionList from "./UnionList"
import PayAdjustments from "@/pages/employees/PayAdjustments"
import RevenueCodeManagerWidget from "@/components/super_components/RevenueCodeManagerWidget"
import EmployeeMassInvite from "./EmployeeMassInvite"
import PersonnelRolesWidget from "@/components/super_components/PersonnelRolesWidget"
import JobsImport from "./JobsImport"
import ContactsImport from "./ContactsImport"
import MaterialImporter from "@/components/material_importer/MaterialImporter"

export default {
	name: "OnboardingWizard",
	components: {
		SetupWizardComponent,
		EmployeeClassifications,
		ChargeOutGroups,
		SettingsUnions,
		UnionList,
		PayAdjustments,
		RevenueCodeManagerWidget,
		EmployeeMassInvite,
		PersonnelRolesWidget,
		JobsImport,
		ContactsImport,
		MaterialImporter
	},
	data() {
		return {
			activeSecIndex: 0,
			activeQIndex: 0,
			questionChangeListener: 0
		}
	},
	computed: {	
		sections() {
			return [
				{
					key: "intro",
					sectionHeading: "Onboarding Wizard",
					nextButtonText: "Let's Go",
					hideSkip: true,
					isCustom: true
				},
				{
					key: "taxes",
					sectionHeading: "Sales Tax",
					apiScope: ["CompanySetting"],
					questions: [
						{
							conditionalQuestion: "Does your company charge and remit HST or other sales taxes?",
							conditionalAnswer: null,
							question: "Set your sales tax up",
							moreInfo: "",
							fields: [
								{
									vmodel: "tax_name",
									label: "Tax Name",
									params: {
										placeholder: "ex. HST"
									}
								},
								{
									vmodel: "tax_percentage",
									label: "Tax %",
									params: {
										placeholder: "ex. 13%",
										type: "number",
										min: 0,
										max: 99,
										step: 0.1
									}
								}
							],
							payload: {
								tax_name: "HST",
								tax_percentage: 13
							},
							setPayload(payload) {
								if (payload && "taxes_json" in payload) {
									let taxes = JSON.parse(payload.taxes_json)
									let taxName = "HST"
									let taxAmount = 13
									Object.keys(taxes).forEach(tax => {
										taxName = tax
										taxAmount = taxes[tax] * 100
									})

									return {
										tax_name: taxName,
										tax_percentage: taxAmount
									}
								}
								else return false
							},
							mutatedPayload(payload) {
								let vm = this

								let taxes = {}
								taxes[payload.tax_name] = payload.tax_percentage / 100

								return {
									taxes_json: JSON.stringify(taxes)
								}
							},
							findEndpoint: "CompanySetting/mine",
							saveEndpoint: "CompanySetting/save"
						}
					]
				},
				{
					key: "smpt",
					sectionHeading: "Sending Email",
					intro: "You can send mail from your own company email server or service. This can sometimes increase delivery rates and prevent outgoing emails from Tradetraks getting sent to spam folders.",
					apiScope: ["CompanySetting"],
					questions: [
						{
							conditionalQuestion: "Do you have a company email server or service?",
							conditionalAnswer: null,
							question: "Fill in your mail server SMTP details",
							moreInfo: "",
							fields: [
								{
									vmodel: "email",
									label: "What email address would you like to send mail from?",
									params: {
										type: 'email'
									}
								},
								{
									vmodel: "password",
									label: "What is the password for that email address?",
									params: {
										type: 'password'
									}
								},
								{
									vmodel: "outgoingServer",
									label: "What is the outgoing mail server address?",
									params: {
										type: 'text',
										placeholder: "Ex. mail.yourcompany.com"
									}
								},
								{
									vmodel: "outgoingPort",
									label: "What port number for outgoing mail?",
									params: {
										type: 'number',
										step: 1,
										placeholder: "465"
									}
								},
							],
							payload: {
								mail_client_active: 1,
								email: "",
								password: "",
								outgoingServer: "",
								outgoingPort: 465
							},
							setPayload(payload) {
								if (payload && "mail_client_json" in payload) {
									let mailSettings = JSON.parse(payload.mail_client_json)

									return {
										email: mailSettings.email,
										password: mailSettings.password,
										outgoingServer: mailSettings.outgoingServer,
										outgoingPort: mailSettings.outgoingPort
									}
								}
								else return false
							},
							saveEndpoint: "CompanySetting/save",
							findEndpoint: "CompanySetting/mine"
						}
					]
				},
				{
					key: "payroll",
					sectionHeading: "Payroll",
					apiScope: ["TimeLogSetting"],
					questions: [
						{
							question: "Fill in some payroll details",
							moreInfo: "",
							fields: [
								{
									vmodel: "interval",
									label: "How often do you pay employees?",
									params: {
										type: 'select',
										options: [
											{text: "Weekly (every 7 days)",value: "weekly"},
											{text: "Bi-Weekly (every 14 days)",value: "biweekly"},
											{text: "Semi-Monthly (twice/month)",value: "semimonthly"},
											{text: "Monthly (once/month)",value: "monthly"}
										]
									}
								},
								{
									vmodel: "intitial_payday",
									label: "What is the start date of your next Payroll?",
									params: {
										type: 'date'
									}
								},
							],
							payload: {
								id: "",
								interval: "",
								intitial_payday: ""
							},
							saveEndpoint: "TimeLogSetting/save",
							findEndpoint: "TimeLogSetting/mine"
						},
						{
							conditionalQuestion: "Do you pay overtime?",
							conditionalAnswer: null,
							question: "Select the overtime rate multipliers you pay",
							moreInfo: "",
							fields: [
								{
									vmodel: "uses_overtime_15x",
									label: "1.5x (Time and a half)",
									params: {
										type: 'boolean'
									}
								},
								{
									vmodel: "uses_overtime_2x",
									label: "2x (Double time)",
									params: {
										type: 'boolean'
									}
								},
								{
									vmodel: "uses_overtime_25x",
									label: "2.5x (Double time and a half)",
									params: {
										type: 'boolean'
									}
								},
							],
							payload: {
								id: "",
								uses_overtime_15x: "",
								uses_overtime_2x: "",
								uses_overtime_25x: ""
							},
							saveEndpoint: "TimeLogSetting/save",
							findEndpoint: "TimeLogSetting/mine"
						}
					]
				},
				{
					key: "chargeOutGroups",
					isCustom: true,
					intro: "Often you may need to bill out multiple employee classifications at a single rate. Setting up Charge Out Groups allows you to assign multiple classifications to a single group, and set a default billing rate for that group.",
					sectionHeading: "Charge Out Groups",
					apiScope: ["ClassificationGroup"]
				},
				{
					key: "unionList",
					isCustom: true,
					sectionHeading: "Compensation Groups",
					apiScope: ["Union"]
				},
				{
					key: "employeeClassifications",
					isCustom: true,
					intro: "Employee Classifications is a list of job positions at your company, which may be paid different hourly rates. Some examples may include: Labourer, Apprentice Level 1-4, Journeyman, Foreman. We've got you started with some basic classifications, which you can rename or change to your needs.</p><p>NOTE: If you work with unions, you will be able to set up different pay rates for each classification within the compenation group wage schedules.",
					sectionHeading: "Employee Classifications",
					apiScope: ["EmployeeClassification","Union"],
				},
				{
					key: "unionSetup",
					isCustom: true,
					intro: "Compensation Schedules belong under a Compensation Group, and allow you to create unique compensation schedules for each classification of a unionized employee, including mileage pay, per diems, and specific hourly pay rates.</p><p>Click into one of the Compensation Groups you created to add a Compensation Schedule to it.",
					sectionHeading: "Compensation Schedules",
					apiScope: ["Union","EmployeeClassification"],
				},
				{
					key: "payAdjustments",
					isCustom: true,
					intro: "Pay adjustments are for when a temporary increase or reduction in pay needs to applied for a particular employee, for example if you offer higher pay for working outside of regular hours, or for hazard pay. You can set up your pay adjustments, and then activate it for particular projects.</p><p>When a pay adjustment is activated, an option on the clock-out page appears for an employee to be able to claim it on his time sheet.",
					sectionHeading: "Pay Adjustments",
					apiScope: ["PayBoost"],
				},
				{
					key: "revenueCodes",
					isCustom: true,
					intro: "Best business practice is for companies to use Revenue Codes. These codes help track different revenues your business receives and help with revenue reporting. If you use revenue codes in your accounting application, you will want to create matching codes in Tradetraks.</p><p>If you do not use codes, then select no and Tradetraks will assign default codes for you. Revenue Codes are a require field in the Tradetraks app.",
					sectionHeading: "Revenue Codes",
					apiScope: ["RevenueCode"],
				},
				{
					key: "contactsImport",
					isCustom: true,
					intro: "Inputting customers into Tradetraks is easy and a great opportunity to clean up your list of customers.  In Tradetraks, the Contacts Network is your 'black book', holding the list of your customers, suppliers, and any other external business or individual you work with.</p><p>Contacts can be either a company, or an individual. Company contacts can have a list of sub-contacts, who are individuals. Contacts can be added to Tradetraks either by manual entry or through importing using a CSV file. If you would like to batch import your contacts using a CSV file, you can do that here. Otherwise click 'Skip'.",
					sectionHeading: "Contacts Import",
					apiScope: ["Contact"]
				},
				{
					key: "projectsImport",
					isCustom: true,
					intro: "Importing your company's projects into TradeTraks empowers you to take full control of your company's workflow.",
					sectionHeading: "Projects Import",
					apiScope: ["Job"]
				},
				{
					key: "servicesImport",
					isCustom: true,
					intro: "Importing your company's services into TradeTraks empowers you to take full control of your company's workflow.",
					sectionHeading: "Services Import",
					apiScope: ["Service"]
				},
				{
					key: "materialsImport",
					isCustom: true,
					intro: "Inputting materials into TradeTraks allows you to easily manage your list of materials. These materials are organized into categories, which can contain subcategories to further break down the list.</p><p>Materials can be added to Tradetraks either by manual entry or through importing a CSV file. If you would like to batch import your materials using a CSV file, you can do that here. Otherwise click 'Skip'.",
					sectionHeading: "Materials Import",
					apiScope: ["Material", "MaterialCategory"]
				},
				{
					key: "personnelRoles",
					isCustom: true,
					intro: "Personnel roles are used in Tradetraks to give a specific user permissions and responsibility to different functions in the app when it comes to Projects and Services. </p><p>Some examples of roles have been seeded to get you started. You are free to edit those or create your own",
					sectionHeading: "Personnel Roles",
					apiScope: ["JobPersonnelRole"]
				},
				{
					key: "addEmployees",
					isCustom: true,
					intro: "For employees to access Tradetraks, they must have their own Tradetraks account, and be attached to your company. Add your employees using the form below, and we'll send them an email to log-in as well as guide them through the steps to set up their profile. They will also be automatically connected to your company.",
					sectionHeading: "Add Employees",
					apiScope: ["User","EmployeeClassification"],
				}
			]
		}
	},
	methods: {
		activeSecIndexChange(index) {
			this.activeSecIndex = index
			this.activeQIndex = 0
		},
		activeQIndexChange(index) {
			this.activeQIndex = index
		},
		unionCreateDone() {
			//remove union setup from list
			let sectionIndex = this.sections.findIndex(section => section.key == 'unionSetup')

			if (this.$store.unions.length == 0) {
				this.includeUnionSetup = false
				
				//if (sectionIndex >= 0)
				//	this.sections.splice(sectionIndex,1)
			}
			else if (sectionIndex == -1) {
				//add union setup from list
				this.includeUnionSetup = true

				/*
				this.sections.splice(6,0,{
					key: "unionSetup",
					isCustom: true,
					intro: "Compensation Schedules allow you to create unique compensation schedules for each classification of a unionized employee, including mileage pay, per diems, and specific hourly pay rates.",
					sectionHeading: "Compensation Schedules",
				})
				*/
			}

			this.questionChangeListener++
		}
	}
}
</script>